import { t } from "@lingui/macro";
import type { PromoLinkClaimResult, UserDto } from "@reactive-resume/dto";
import { useEffect } from "react";

import { toast } from "@/client/hooks/use-toast";
import { queryClient } from "@/client/libs/query-client";
import { claimPromoLink } from "@/client/services/promo-link/claim";
import { clearPendingPromoLink, getPendingPromoLink } from "@/client/services/promo-link/storage";

type Props = {
  user: UserDto;
};

let inFlight = false;

// Le succès s'affiche en fenêtre via la notification CREDIT_GRANTED (NotificationPopupHost).
const announce = ({ status }: PromoLinkClaimResult) => {
  if (status === "claimed") {
    void queryClient.invalidateQueries({ queryKey: ["balance-credit"] });
    void queryClient.invalidateQueries({ queryKey: ["user-space", "account-overview"] });
    void queryClient.invalidateQueries({ queryKey: ["notifications"] });
  } else if (status === "already_claimed") {
    toast({ variant: "info", title: t`Cette offre est déjà sur votre compte.` });
  } else if (status === "expired") {
    toast({
      variant: "warning",
      title: t`Cette offre est terminée.`,
      description: t`Découvrez nos formules pour télécharger votre CV.`,
    });
  } else {
    toast({ variant: "warning", title: t`Ce lien n'est plus valide.` });
  }
};

// Réclame le crédit du lien promotionnel une fois le compte connecté et le pays choisi.
export const PromoLinkClaimer = ({ user }: Props) => {
  const ready = Boolean(user.countryPreferenceSetAt);

  useEffect(() => {
    const code = getPendingPromoLink();
    if (!ready || !code || inFlight) return;

    inFlight = true;
    claimPromoLink(code)
      .then((result) => {
        clearPendingPromoLink();
        announce(result);
      })
      .catch(() => {
        // Code conservé : nouvel essai au prochain chargement.
      })
      .finally(() => {
        inFlight = false;
      });
  }, [ready, user.id]);

  return null;
};
